import {
  clearMemberToken,
  getMemberToken,
  MemberPortalApi,
  setMemberToken,
} from "@/api/perkpoint/memberPortal";

const SESSION_KEY = "memberPortalV2Session";

export type OtpChannel = "email" | "mobile";

export type MemberSession = {
  memberId: string;
  fullName: string;
  initials: string;
  contact: string;
  channel: OtpChannel;
  token: string;
};

export async function requestLoginCode(
  contact: string,
  channel: OtpChannel,
  externalMemberId?: string,
) {
  const programId = (import.meta.env.VITE_PERKPOINT_PROGRAM_ID as string | undefined)?.trim();
  return MemberPortalApi.requestOtp({
    channel,
    contact: contact.trim(),
    ...(programId ? { program_id: programId } : {}),
    ...(externalMemberId ? { external_member_id: externalMemberId } : {}),
  });
}

export async function loginWithOtp(
  contact: string,
  channel: OtpChannel,
  requestId: string,
  code: string,
): Promise<MemberSession> {
  const result = await MemberPortalApi.verifyOtp({ request_id: requestId, code });
  const session: MemberSession = {
    memberId: result.session.member_id,
    fullName: result.session.full_name,
    initials: result.session.initials,
    contact: contact.trim(),
    channel,
    token: result.token,
  };
  setMemberToken(result.token);
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.removeItem(SESSION_KEY);
  return session;
}

export async function logoutMember() {
  if (getMemberToken()) {
    try {
      await MemberPortalApi.logout();
    } catch {
      // The local session still goes away if the API is unreachable.
    }
  }
  clearMemberToken();
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
}

export function getMemberSession(): MemberSession | null {
  const raw = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
  const token = getMemberToken();
  if (!raw || !token) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<MemberSession>;
    if (!parsed.memberId || !parsed.fullName) {
      clearMemberToken();
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return {
      memberId: parsed.memberId,
      fullName: parsed.fullName,
      initials: parsed.initials || parsed.fullName.slice(0, 2).toUpperCase(),
      contact: parsed.contact || "",
      channel: parsed.channel === "mobile" ? "mobile" : "email",
      token,
    };
  } catch {
    clearMemberToken();
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}
